import { useOverview, usePhotos } from "web/hooks/data"
import TileSet from "web/components/TileSet/TileSet"
import CurrentTemplateManager from "./CurrentTemplateManager"
import { UserMessageTile } from "web/components/Tiles"
import { UseOverviewUtils } from "web/utils/hooks/useOverview.utils"
import { DialogCommands, useDialogModal } from "web/hooks/modals"

export default function ObjectOverview({
  selectedOverviewId,
  editPath,
  exportPath,
  maxColumns = 2,
  organizational = false,
  subjectType,
  subjectId,
  canEdit,
}) {
  if (selectedOverviewId) {
    return (
      <Overview
        subjectType={subjectType}
        subjectId={subjectId}
        selectedOverviewId={selectedOverviewId}
        maxColumns={maxColumns}
        canEdit={canEdit}
        organizational={organizational}
        editPath={editPath}
        exportPath={exportPath}
      />
    )
  } else {
    return <OverviewBlankSlate />
  }
}

function Overview({
  subjectType,
  subjectId,
  selectedOverviewId,
  maxColumns,
  canEdit,
  organizational,
  editPath,
  exportPath,
}) {
  const overviewFetchParams = UseOverviewUtils.prepareRequestProperties(
    false,
    selectedOverviewId
  )

  const currentOverview = useOverview({ overviewFetchParams })

  const photos = usePhotos({ subjectType, subjectId })

  function ResetMessage() {
    return (
      <div>
        Are you sure you want to reset back to the template?
        <div className="mt-4">
          Selecting Reset will restore the Overview to it's original state and
          all changes will be{" "}
          <span className="font-bold">lost for all users</span>.
        </div>
        {currentOverview.tile_names_with_content_loss.length > 0 && (
          <div className="mt-4">
            The content of the following tiles will be{" "}
            <span className="font-bold">permanently deleted</span>:
            <ul className="ml-6 mt-1 list-disc">
              {currentOverview.tile_names_with_content_loss.map(
                (name, index) => {
                  return (
                    <li key={index}>
                      <span className="font-bold">{name}</span>
                    </li>
                  )
                }
              )}
            </ul>
          </div>
        )}
      </div>
    )
  }

  const confirmResetToTemplate = useDialogModal({
    title: "Reset to Template",
    heightPixels: 400,
    prompt: <ResetMessage />,
    commands: [
      { label: "Reset", variant: "primary_danger", response: true },
      DialogCommands.Cancel,
    ],
  })

  async function handleResetToTemplate() {
    const result = await confirmResetToTemplate()

    if (result?.response) {
      await currentOverview.handleTemplateReset()
    }
  }

  return (
    <div className="max-w-[1080px] pb-4">
      <CurrentTemplateManager
        currentOverview={currentOverview}
        handleTemplateReset={handleResetToTemplate}
        canSelect={organizational ? false : canEdit}
        canEdit={canEdit}
        subjectType={subjectType}
        subjectId={subjectId}
        overviewFetchParams={overviewFetchParams}
        editPath={editPath}
        exportPath={exportPath}
      />
      <div className="mt-3">
        {currentOverview.tiles.length === 0 ? (
          <TileBlankSlate
            message={
              'No tiles have been added to this Overview. You can add one by clicking the "Add Tile" button above.'
            }
          />
        ) : (
          <TileSet
            tiles={currentOverview.tiles}
            maxColumns={maxColumns}
            onMoveTile={currentOverview.handleMoveTile}
            onResizeTile={currentOverview.handleResizeTile}
            overview={currentOverview}
            canEdit={canEdit}
            photos={photos}
            subjectType={subjectType}
            subjectId={subjectId}
            modifiedFromTemplate={currentOverview.modified_from_template}
          />
        )}
      </div>
    </div>
  )
}

export function DealOrganizationOverview({ dealId, overviewId }) {
  const overviewFetchParams = UseOverviewUtils.prepareRequestProperties(
    false,
    overviewId
  )

  const currentOverview = useOverview({ overviewFetchParams })

  const photos = usePhotos({ subjectType: "deal", subjectId: dealId })

  return (
    <div className="max-w-[1080px] pb-4">
      <div className="mt-3">
        {currentOverview.tiles.length === 0 ? (
          <TileBlankSlate message="No tiles have been added to this Overview." />
        ) : (
          <TileSet
            tiles={currentOverview.tiles}
            maxColumns={2}
            onMoveTile={currentOverview.handleMoveTile}
            onResizeTile={currentOverview.handleResizeTile}
            overview={currentOverview}
            canEdit={false}
            photos={photos}
            subjectType={"deal"}
            modifiedFromTemplate={currentOverview.modified_from_template}
          />
        )}
      </div>
    </div>
  )
}

function OverviewBlankSlate() {
  return (
    <UserMessageTile message={"There are no Overview templates available."} />
  )
}

function TileBlankSlate({ message }) {
  return <UserMessageTile message={message} />
}
